import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, DatePicker, Icon, Form, message, Tooltip, Tag } from "antd";
import moment from "moment";
import { uploadCustomsClearanceFile, deleteOrderPic, updateOrderDetails_v2 } from "../../api/OrdersApi";
import { CheckMarkIcon, CircleCheckIcon, CustomsOfficerVerifedIcon, DotedCircleIcon,dotedCircleSvg, EdiIcon, EditPennIcon, SelectedTickMarkIcon, TrashIcon } from "../../common/CustomIcon";
import UploadButton_v2 from "./UploadButton_v2";
import "./CustomsInformation.scss";
import { CustomsOfficerIcon } from "../../common/CustomIcon";
import Button_v2 from "./Button_v2";

const CUSTOMS_DOC_TYPES = [
  { code: "DELIVERY_ORDER", label: "delivery order" },
  { code: "CUSTOMS_RELEASE_DOCUMENT", label: "customs release document" },
  { code: "COPY_OF_CHECK_ACH_NUMBER", label: "copy of check" },
  { code: "CUSTOMS_STATUS_LOG", label: "customs status log (from descartes)" },
  { code: "MAWB_HAWB", label: "MAWB/HAWB" },
  { code: "CARGO_MANIFEST", label: "cargo manifest" }
];

const CustomsInformation = ({
  getFieldDecorator,
  orderDetails,
  currentOrder,
  onCustomsDataChange,
  viewMode = false,
  currentOrg = null,
  refreshDetails = () => {},
}) => {
  const [documents, setDocuments] = useState([]);
  const [stagedDocs, setStagedDocs] = useState([]);
  const [stagedDocsUploadedMap, setStagedDocsUploadedMap] = useState({});
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [previewFileType, setPreviewFileType] = useState(null);
  const [show, setShow] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const details = orderDetails || {};
  const orderId = details.id || currentOrder?.id || "";
  const locations = currentOrder?.locations || details.locations || [];
  const locationId = locations[0]?.location_id || locations[0]?.id || "";

  // NEW: track the actual date values so they can be sent up via onCustomsDataChange
  const [freightArrivalDateVal, setFreightArrivalDateVal] = useState(details.freight_arrival_date || null);
  const [generalOrderDateVal, setGeneralOrderDateVal] = useState(details.general_order_date || null);
  const [lastFreeDayVal, setLastFreeDayVal] = useState(details.last_free_day || null);
  const [customsClearedDateVal, setCustomsClearedDateVal] = useState(details.customs_cleared_date || null);
  const [isCustomsCleared, setIsCustomsCleared] = useState(!!details.is_customs_cleared);

  // Check if every document type in CUSTOMS_DOC_TYPES has at least one uploaded or staged document
  const allDocsUploaded = CUSTOMS_DOC_TYPES.every((type) =>
    documents.some((d) => d.pic_code === type.code) ||
    stagedDocs.some((d) => d.pic_code === type.code)
  );

  useEffect(() => {
    onCustomsDataChange?.({
      documents,
      stagedDocs,
      stagedDocsUploadedMap,
      isUploadingAll,
      previewVisible,
      previewFileUrl,
      previewFileType,
      show,
      selectedDocs,
    // NEW
      freight_arrival_date: freightArrivalDateVal ? moment(freightArrivalDateVal).format("YYYY-MM-DD") : null,
      general_order_date: generalOrderDateVal ? moment(generalOrderDateVal).format("YYYY-MM-DD") : null,
      last_free_day: lastFreeDayVal ? moment(lastFreeDayVal).format("YYYY-MM-DD") : null,
      customs_cleared_date: customsClearedDateVal ? moment(customsClearedDateVal).format("YYYY-MM-DD") : null,
  });
}, [
  documents,
  stagedDocs,
  stagedDocsUploadedMap,
  isUploadingAll,
  previewVisible,
  previewFileUrl,
  previewFileType,
  show,
  selectedDocs,
  // NEW
  freightArrivalDateVal,
  generalOrderDateVal,
  lastFreeDayVal,
  customsClearedDateVal,
]);

useEffect(() => {
  setFreightArrivalDateVal(orderDetails?.freight_arrival_date || null);
  setGeneralOrderDateVal(orderDetails?.general_order_date || null);
  setLastFreeDayVal(orderDetails?.last_free_day || null);
  setCustomsClearedDateVal(orderDetails?.customs_cleared_date || null);
  setIsCustomsCleared(!!orderDetails?.is_customs_cleared || null);
}, [orderDetails?.freight_arrival_date, orderDetails?.general_order_date, orderDetails?.last_free_day, orderDetails?.customs_cleared_date]);

  useEffect(() => {
    const docs = [];
    const locations = currentOrder?.locations || orderDetails?.locations || [];
    if (locations.length > 0) {
      for (const loc of locations) {
        if (loc?.documents) {
          for (const doc of loc.documents) {
            const docCode = (doc?.pic_code || "").toUpperCase();
            const docTitle = (doc?.pic_title || "").toUpperCase();
            for (const type of CUSTOMS_DOC_TYPES) {
              if (
                docCode === type.code ||
                docTitle === type.label.toUpperCase() ||
                (type.code === "DELIVERY_ORDER" && docCode === "IMPORT")
              ) {
                if (!docs.some((d) => d.pic_code === type.code)) {
                  docs.push({ ...doc, pic_code: type.code });
                }
              }
            }
          }
        }
      }
    }
    setDocuments(docs);
  }, [orderDetails, currentOrder]);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const uploadSingleCustomsFile = (stagedDoc) => {
    setIsUploadingAll(true);

    const payload = {
      refer: "order",
      order_ids: [orderId],
      location_id: locationId,
      pictures: [
        {
          refer_ids: [orderId],
        item_pictures: [
          {
            captured_at: stagedDoc.captured_at,
            picture_obj: stagedDoc.picture_obj,
            ack_id: stagedDoc.ack_id,
            pic_title: stagedDoc.pic_title,
            pic_code: stagedDoc.pic_code,
            image_type: "import",
            src: stagedDoc.src,
          },
        ],
      },
    ],
    pod_pic_settings: [
      {
        pic_code: stagedDoc.pic_code,
        mandatory: true,
        mandatory_for: ["driver", "billing"],
        required_for: "both",
      },
    ],
  };

    uploadCustomsClearanceFile(payload)
      .then((res) => {
        if (res && res.success) {
        setStagedDocsUploadedMap((prev) => ({
          ...prev,
          [stagedDoc.pic_code]: true,
        }));
        message.success(`${stagedDoc.pic_title} uploaded successfully`);
        refreshDetails();
      } else {
        message.error(res?.errors || "Failed to upload document");
        setStagedDocs((prev) => prev.filter((doc) => doc.ack_id !== stagedDoc.ack_id));
      }
    })
    .catch((err) => {
      console.error("Upload error:", err);
      message.error("An error occurred during upload");
      setStagedDocs((prev) => prev.filter((doc) => doc.ack_id !== stagedDoc.ack_id));
    })
      .finally(() => {
        setIsUploadingAll(false);
      });
  };

  const handleChange = async (info, record = {}) => {
    if (info.file.status !== "uploading") {
      const docTypeCode = record.pic_code;
      const docTypeLabel =
        CUSTOMS_DOC_TYPES.find((t) => t.code === docTypeCode)?.label || docTypeCode;
      const file = info.file.originFileObj || info.file;
      const ackId =
        Math.random().toString(36).substring(2, 15) + "_" + Date.now() + "_n";

      try {
        const base64Data = await getBase64(file);
        const stagedDoc = {
          picture_obj: file,
          captured_at: new Date().toISOString(),
          ack_id: ackId,
          pic_title: docTypeLabel,
          pic_code: docTypeCode,
          image_type: "import",
          picture_type: "import",
          isNew: true,
          fileType: file.type || "",
          fileName: file.name || "",
          src: base64Data,
        };

      setStagedDocs((prev) => [...prev, stagedDoc]);
        if (viewMode) {
        uploadSingleCustomsFile(stagedDoc);
      }
      } catch (error) {
        console.error("Error while converting file:", error);
      }
    }
  };
  const toggleSelectDocument = (ack_id, pictures_id, check) => {
    if (check) {
      return;
    }
    const docObj = { ack_id, pictures_id };
    setSelectedDocs((prevSelectedDocs) => {
      const isAlreadySelected = prevSelectedDocs.some(
        (d) => d.ack_id === ack_id
      );
      if (isAlreadySelected) {
        return prevSelectedDocs.filter((d) => d.ack_id !== ack_id);
      }
      return [...prevSelectedDocs, docObj];
    });
  };

  const selectAll = () => {
    setSelectedDocs(
      [...documents, ...stagedDocs].map((doc) => ({
        ack_id: doc.ack_id,
        pictures_id: doc.id,
      }))
    );
  };

  const deselectAll = () => {
    setSelectedDocs([]);
  };

  const handleDelete = () => {
    const selectedAckIds = selectedDocs.map((doc) => doc.ack_id);
    if (selectedAckIds.length === 0) return;

    const inDocuments = selectedAckIds.filter((id) =>
      documents.some((doc) => doc.ack_id === id)
    );
    const inStaged = selectedAckIds.filter((id) =>
      stagedDocs.some((doc) => doc.ack_id === id)
    );

    if (inStaged.length > 0) {
      setStagedDocs((prev) => prev.filter((doc) => !inStaged.includes(doc.ack_id)));
    }

    if (inDocuments.length > 0 && orderId) {
      const data = {
        refer: "order",
        refer_id: orderId,
        ack_id: inDocuments.join(","),
        account_id:
          currentOrder?.account_id ||
          currentOrder?.account?.account_id ||
          details?.account_id ||
          "",
        organization_id:
          currentOrder?.organization_id || details?.organization_id || "",
      };

      deleteOrderPic(data)
        .then((res) => {
          if (res && res.success) {
            setDocuments((prev) =>
              prev.filter((doc) => !inDocuments.includes(doc.ack_id))
            );
            message.success("Document(s) deleted successfully");
          } else {
            message.error(res?.errors || "Failed to delete document");
          }
        })
        .catch((err) => {
          console.error("Delete error:", err);
          message.error("An error occurred while deleting document");
        })
        .finally(() => {
          setSelectedDocs([]);
        });
    } else {
      setSelectedDocs([]);
    }
  };

  const renderSelectionHeader = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
      <div
        style={{
          ...styles.selectionBlock,
          cursor: "pointer",
        }}
        onClick={() =>
          selectedDocs.length === [...documents, ...stagedDocs].length
            ? deselectAll()
            : selectAll()
        }
      >
        <Icon
          type={
            selectedDocs.length === [...documents, ...stagedDocs].length
              ? "check"
              : "minus"
          }
          style={{
            color: "#fff",
            position: "relative",
            top: "-5px",
            left: "3px",
            fontSize: "12px",
          }}
        />
      </div>
      <span style={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>
        {selectedDocs.length} Selected
      </span>
    </div>
  );

  const freightDate = details.freight_arrival_date || null;
  const generalDate = details.general_order_date || null;
  const lastFreeDate = details.last_free_day || null;
  const clearanceDate = details.customs_cleared_date || null;

  const startEdit = () => {
    setFreightArrivalDateVal(freightDate || null);
    setGeneralOrderDateVal(generalDate || null);
    setLastFreeDayVal(lastFreeDate || null);
    setCustomsClearedDateVal(clearanceDate || null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setFreightArrivalDateVal(freightDate || null);
    setGeneralOrderDateVal(generalDate || null);
    setLastFreeDayVal(lastFreeDate || null);
    setCustomsClearedDateVal(clearanceDate || null);
    setIsEditing(false);
  };

  // Adds `days` business days to a date, skipping Sat/Sun
  const addBusinessDays = (date, days) => {
    let result = moment(date);
    let added = 0;
    while (added < days) {
      result = result.add(1, "days");
      const dow = result.isoWeekday(); // 6 = Sat, 7 = Sun
      if (dow !== 6 && dow !== 7) {
        added++;
      }
    }
    return result;
  };

  const addDays = (date, days) => {
  return moment(date).add(days, "days");
};

const handleArrivalDateCascade = (date) => {
  if (!date) {
    setGeneralOrderDateVal(null);
    setLastFreeDayVal(null);
    return;
  }

  const generalOrder = addDays(date, 14);
  const lastFree = addBusinessDays(generalOrder, 3);

  setGeneralOrderDateVal(generalOrder.format("MM-DD-YYYY"));
  setLastFreeDayVal(lastFree.format("MM-DD-YYYY"));
};

  const handleSave = async () => {
    const payload = {
      customer_order_params: {
        customer_order_id: currentOrder?.id || orderId,
        warehouse_id:
          currentOrder?.customer_order_details?.warehouse_id || details?.warehouse_id || "",
        organization_id:
          currentOrg?.id ||
          currentOrder?.customer_order_details?.organization_id ||
          currentOrder?.organization_id ||
          details?.organization_id ||
          "",
      },
      options_block_params: {
        freight_arrival_date: freightArrivalDateVal,
        general_order_date: generalOrderDateVal,
        last_free_day: lastFreeDayVal,
        customs_cleared_date: customsClearedDateVal,
        is_customs_cleared: !!customsClearedDateVal,
      },
    };

    try {
      const result = await updateOrderDetails_v2(
        payload.customer_order_params.customer_order_id,
        payload
      );
      if (result?.success) {
        message.success("Customs dates updated successfully");
        setIsCustomsCleared(!!customsClearedDateVal);
        setIsEditing(false);
        refreshDetails();
      } else {
        message.error(result?.errors || "Failed to update customs dates");
      }
    } catch (err) {
      console.error("Save customs dates error:", err);
      message.error("An error occurred while updating customs dates");
    }
  };

  const handleMarkCleared = async () => {

    const today = moment().format("MM/DD/YYYY");
    setCustomsClearedDateVal(today);
    setIsCustomsCleared(true);

    if (viewMode) {
    const payload = {
      customer_order_params: {
        customer_order_id: currentOrder?.id || orderId,
        warehouse_id:
          currentOrder?.customer_order_details?.warehouse_id || details?.warehouse_id || "",
        organization_id:
          currentOrg?.id ||
          currentOrder?.customer_order_details?.organization_id ||
          currentOrder?.organization_id ||
          details?.organization_id ||
          "",
      },
      options_block_params: {
        customs_cleared_date: today,
        is_customs_cleared: true,
      },
    };

    try {
      const result = await updateOrderDetails_v2(
        payload.customer_order_params.customer_order_id,
        payload
      );
      if (result?.success) {
        message.success("Order marked as cleared");
        refreshDetails();
      } else {
        message.error(result?.errors || "Failed to mark order as cleared");
      }
    } catch (err) {
      console.error("Mark cleared error:", err);
      message.error("An error occurred while marking order as cleared");
    }
  }
  };

  const handleRemoveClearance = async () => {
    setCustomsClearedDateVal(null);
    setIsCustomsCleared(false);

    if (viewMode) {
    const payload = {
      customer_order_params: {
        customer_order_id: currentOrder?.id || orderId,
        warehouse_id:
          currentOrder?.customer_order_details?.warehouse_id || details?.warehouse_id || "",
        organization_id:
          currentOrg?.id ||
          currentOrder?.customer_order_details?.organization_id ||
          currentOrder?.organization_id ||
          details?.organization_id ||
          "",
      },
      options_block_params: {
        customs_cleared_date: null,
        is_customs_cleared: false,
      },
    };

    try {
      const result = await updateOrderDetails_v2(
        payload.customer_order_params.customer_order_id,
        payload
      );
      if (result?.success) {
        setIsEditing(false);
        message.success("Clearance removed");
        refreshDetails();
      } else {
        message.error(result?.errors || "Failed to remove clearance");
      }
    } catch (err) {
      console.error("Remove clearance error:", err);
      message.error("An error occurred while removing clearance");
    }
  }
  };

  const formatDateText = (dateVal) =>
    dateVal ? moment(dateVal).format("MM/DD/YYYY") : "—";

  return (
    <Card bordered={false} className="customs-card" bodyStyle={{ padding: 16 }}>
      <Row
        type="flex"
        justify="space-between"
        align="middle"
        className="customs-header"
      >
        <Col>
          <div className="card-title">
            <CustomsOfficerIcon />
            Customs Information
          </div>
        </Col>
        <Col>
          {viewMode ? (
            isEditing ? (
              <div style={{ display: "flex", gap: "8px" }}>
                {isCustomsCleared && (
                    <Button_v2 className="outline small-btn" onClick={handleRemoveClearance}>
                      Remove clearance
                    </Button_v2>
                )}
                <Button_v2 className="outline small-btn" onClick={cancelEdit}>
                  Cancel
                </Button_v2>
                <Button_v2 className="raised small-btn" onClick={handleSave}>
                  Save
                </Button_v2>
              </div>
            ) : (
                <div style={{ display: "flex", gap: "8px" }}>
                  {isCustomsCleared ? (
                      <Tag className="cleared-tag">
                        <CustomsOfficerVerifedIcon />
                        <span>Cleared for Release</span>
                      </Tag>
                  ) : (
                        <span>
                          <Button_v2
                          className="outline small-btn"
                            onClick={handleMarkCleared}
                          >
                            <CheckMarkIcon width="19px" height="19px" color="#000000" style={{ marginRight: "0px" }} />
                            <span style={{ marginRight: "6px", position: "relative", top: "-1px" }}>Mark Cleared</span>
                          </Button_v2>
                      </span>
                  )}
                  <Button_v2 className="outline small-btn" onClick={startEdit}>
                    Edit
                  </Button_v2>
              </div>
            )
          ) : (
              <div style={{ display: "flex", gap: "8px" }}>
                {isCustomsCleared ? (
                  <Button_v2 className="outline small-btn" onClick={handleRemoveClearance}>
                    Remove clearance
                  </Button_v2>
                ) : (
                      <Button_v2
                      className="outline small-btn"
                        onClick={handleMarkCleared}
                      >
                        <CheckMarkIcon width="19px" height="19px" color="#000000" style={{ marginRight: "0px" }} />
                        <span style={{ marginRight: "6px", position: "relative", top: "-1px" }}>Mark Cleared</span>
                    </Button_v2>
                )}
              </div>
          )}
        </Col>
      </Row>

      <Row gutter={16} className="date-row">
        <Col span={6}>
          <label>Arrival Date</label>
          {viewMode && !isEditing ? (
            <div>{formatDateText(freightDate)}</div>
          ) : (
            <Form.Item className="custom-form-item" name="freight_arrival_date">
              {getFieldDecorator ? getFieldDecorator("freight_arrival_date", {
                initialValue: freightDate ? moment(freightDate) : null,
              })(
                <DatePicker
                  format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  onChange={(date, dateString) => {
                    setFreightArrivalDateVal(dateString || null);
                    handleArrivalDateCascade(date);
                  }}
                />
              ) : (
                <DatePicker
                      format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                      placeholder="MM-DD-YYYY"
                  value={freightArrivalDateVal ? moment(freightArrivalDateVal) : null}
                  onChange={(date, dateString) => {
                    setFreightArrivalDateVal(dateString || null);
                    handleArrivalDateCascade(date);
                  }}
                />
              )}
            </Form.Item>
          )}
        </Col>
        <Col span={6}>
          <label>General Order Date</label>
          {viewMode && !isEditing ? (
            <div>{formatDateText(generalDate)}</div>
          ) : (
            <Form.Item
              className="custom-form-item"
              name="general_order_date"
              key={generalOrderDateVal || "general_order_date_empty"}
            >
              {getFieldDecorator ? getFieldDecorator("general_order_date", {
                initialValue: generalOrderDateVal ? moment(generalOrderDateVal) : (generalDate ? moment(generalDate) : null),
              })(
                <DatePicker
                  format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  onChange={(date, dateString) => setGeneralOrderDateVal(dateString || null)}
                />
              ) : (
                  <DatePicker
                      format="MM-DD-YYYY" 
                      style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  value={generalOrderDateVal ? moment(generalOrderDateVal) : null}
                  onChange={(date, dateString) => setGeneralOrderDateVal(dateString || null)}
                />
              )}
            </Form.Item>
          )}
        </Col>

        <Col span={6}>
          <label>Last Free Date</label>
          {viewMode && !isEditing ? (
            <div>{formatDateText(lastFreeDate)}</div>
          ) : (
            <Form.Item
              className="custom-form-item"
              name="last_free_day"
              key={lastFreeDayVal || "last_free_day_empty"}
            >
              {getFieldDecorator ? getFieldDecorator("last_free_day", {
                initialValue: lastFreeDayVal ? moment(lastFreeDayVal) : (lastFreeDate ? moment(lastFreeDate) : null),
              })(
                <DatePicker
                  format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                  placeholder="MM-DD-YYYY"
                  onChange={(date, dateString) => setLastFreeDayVal(dateString || null)}
                />
              ) : (
                <DatePicker
                      format="MM-DD-YYYY" 
                  style={{ width: "100%" }}
                      placeholder="MM-DD-YYYY"
                  value={lastFreeDayVal ? moment(lastFreeDayVal) : null}
                  onChange={(date, dateString) => setLastFreeDayVal(dateString || null)}
                />
              )}
            </Form.Item>
          )}
        </Col>
        <Col span={6}>
          <label>Clearance</label>
          {viewMode && !isEditing ? (
            isCustomsCleared ? <div>{formatDateText(clearanceDate)}</div> :
              <div className="pending-badge">
                <span className="pending-circle"></span>
                <span className="pending-text">Pending</span>
              </div>
          ) : (
                <div>
                  <Form.Item
                    className="required-star-right custom-form-item"
                    name="customs_cleared_date"
                    key={customsClearedDateVal || "cleared_empty"}
                  >
                    {getFieldDecorator ? getFieldDecorator("customs_cleared_date", {
                      initialValue: customsClearedDateVal ? moment(customsClearedDateVal, "MM/DD/YYYY") : null
                    })(
                      <DatePicker
                        format="MM-DD-YYYY"
                        style={{ width: "100%" }}
                        placeholder="MM-DD-YYYY"
                        onChange={(date, dateString) => {
                          setCustomsClearedDateVal(dateString || null);
                          setIsCustomsCleared(!!dateString);
                        }}
                      />
                    ) : (
                      <DatePicker
                        format="MM-DD-YYYY" 
                        style={{ width: "100%" }}
                        placeholder="MM-DD-YYYY"
                        value={customsClearedDateVal ? moment(customsClearedDateVal, "MM/DD/YYYY") : null}
                        onChange={(date, dateString) => {
                          setCustomsClearedDateVal(dateString || null);
                          setIsCustomsCleared(!!dateString);
                        }}
                      />
                    )}
                  </Form.Item>
              </div>
          )}
        </Col>

      </Row>

      <div className="documents-wrapper">
        <Row
          type="flex"
          justify="space-between"
          align="middle"
          className="document-header"
        >
          <Col>
            {selectedDocs.length > 0 ? (
              renderSelectionHeader()
            ) : (
                <div className="document-heading">Customs Documents</div>
            )}
          </Col>
          <Col>
            {selectedDocs.length > 0 ? (
              <Tooltip title="Delete">
                <div className="trash-action" data-testid="trash-button">
                  <TrashIcon
                    styling={{ width: "19px", height: "19px" }}
                    color="#D23B71"
                    style={{ cursor: "pointer" }}
                    onClick={handleDelete}
                  />
                </div>
              </Tooltip>
            ) : (
              <div style={{ display: "flex", gap: "8px" }}>
                  {/**<Button_v2 className="outline small-btn">Map Individual Pages</Button_v2>**/}
                </div>
            )}
          </Col>
        </Row>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "24px",
            flexWrap: "wrap",
          }}
        >
          {CUSTOMS_DOC_TYPES.map((type) => (
            <UploadButton_v2
              key={type.code}
              label={type.label}
              docCode={type.code}
              documents={documents}
              stagedDocs={stagedDocs}
              selectedDocs={selectedDocs}
              handleChange={handleChange}
              toggleSelectDocument={toggleSelectDocument}
              predefinedDocsCode={CUSTOMS_DOC_TYPES.map((t) => t.code)}
              onPrint={() => { }}
              requestedAs=""
              setIsPODemailModalVisible={() => { }}
              currentOrder={currentOrder || orderDetails}
              setPreviewVisible={setPreviewVisible}
              previewVisible={previewVisible}
              setPreviewFileUrl={setPreviewFileUrl}
              previewFileUrl={previewFileUrl}
              setPreviewFileType={setPreviewFileType}
              previewFileType={previewFileType}
              refreshDetails={() => { }}
              show={show}
              setShow={setShow}
              locObj={locations[0] || {}}
              isShipper={false}
              previewDocs={[]}
            />
          ))}
        </div>
      </div>
    </Card>
  );
};

const styles = {
  selectionBlock: {
    width: "18px",
    height: "18px",
    borderRadius: "2px",
    backgroundColor: "#576EC1",
    transition: "all 0.3s ease-in-out",
  },
};

export default CustomsInformation;
